import React from 'react';
import { useParams } from 'react-router-dom';
import { getPostById, getAllPosts } from '../utils/markdown';
import { BlogPost as BlogPostType } from '../utils/markdown';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { FiCalendar, FiUser, FiTag, FiArrowLeft, FiArrowRight } from 'react-icons/fi';
import { Link } from 'react-router-dom';

export const BlogPost: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [post, setPost] = React.useState<BlogPostType | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [previousPost, setPreviousPost] = React.useState<BlogPostType | null>(null);
  const [nextPost, setNextPost] = React.useState<BlogPostType | null>(null);

  React.useEffect(() => {
    const fetchPostAndNeighbors = async () => {
      if (!id) return;
      console.log('Fetching post with ID:', id);
      const [fetchedPost, allPosts] = await Promise.all([
        getPostById(id),
        getAllPosts(),
      ]);
      console.log('Fetched post:', fetchedPost);
      setPost(fetchedPost || null);

      // Determine previous/next based on sorted posts (getAllPosts sorts by date desc)
      const currentIndex = allPosts.findIndex((p) => p.id === id);
      if (currentIndex !== -1) {
        const newerIndex = currentIndex - 1; // previous (newer) in descending list
        const olderIndex = currentIndex + 1; // next (older)
        setPreviousPost(newerIndex >= 0 ? allPosts[newerIndex] : null);
        setNextPost(olderIndex < allPosts.length ? allPosts[olderIndex] : null);
      } else {
        setPreviousPost(null);
        setNextPost(null);
      }

      setLoading(false);
    };
    fetchPostAndNeighbors();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white dark:bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-white dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Post not found</h1>
          <Link to="/blog" className="text-blue-500 hover:text-blue-600">
            Return to blog
          </Link>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="min-h-screen bg-white dark:bg-gray-900"
    >
      {/* Back button */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Link
          to="/blog"
          className="inline-flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-8 group text-lg"
        >
          <FiArrowLeft className="mr-2 group-hover:-translate-x-1 transition-transform" />
          Back to Blog
        </Link>
      </div>

      {/* Article header */}
      <article className="max-w-4xl mx-auto px-4 py-8">
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 dark:text-white mb-8 leading-tight">
            {post.title}
          </h1>
          
          <div className="flex flex-wrap items-center gap-6 text-base text-gray-600 dark:text-gray-400 mb-10">
            <div className="flex items-center">
              <FiCalendar className="mr-2 w-5 h-5" />
              {format(new Date(post.date), 'MMMM d, yyyy')}
            </div>
            <div className="flex items-center">
              <FiUser className="mr-2 w-5 h-5" />
              {post.author}
            </div>
            <div className="flex items-center">
              <FiTag className="mr-2 w-5 h-5" />
              {post.category}
            </div>
          </div>

          <div className="flex flex-wrap gap-3 mb-10">
            {post.tags.map((tag) => (
              <span
                key={tag}
                className="px-4 py-1.5 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-100 rounded-full text-base"
              >
                {tag}
              </span>
            ))}
          </div>
        </motion.div>

        {/* Article content */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="prose prose-xl dark:prose-invert max-w-none 
            prose-headings:text-gray-900 dark:prose-headings:text-white 
            prose-h1:text-4xl prose-h1:font-bold prose-h1:mb-8
            prose-h2:text-3xl prose-h2:font-bold prose-h2:mb-6
            prose-h3:text-2xl prose-h3:font-bold prose-h3:mb-4
            prose-p:text-gray-600 dark:prose-p:text-gray-300 prose-p:text-lg prose-p:leading-relaxed prose-p:mb-6
            prose-a:text-blue-600 dark:prose-a:text-blue-400 prose-a:no-underline hover:prose-a:underline
            prose-code:text-gray-900 dark:prose-code:text-gray-100 prose-code:bg-gray-100 dark:prose-code:bg-gray-800 prose-code:px-2 prose-code:py-1 prose-code:rounded
            prose-pre:bg-gray-100 dark:prose-pre:bg-gray-800 prose-pre:text-gray-900 dark:prose-pre:text-gray-100 prose-pre:p-6 prose-pre:rounded-lg
            prose-li:text-gray-600 dark:prose-li:text-gray-300 prose-li:text-lg prose-li:mb-2
            prose-strong:text-gray-900 dark:prose-strong:text-white prose-strong:font-bold"
        >
          <div dangerouslySetInnerHTML={{ __html: post.htmlContent || '' }} />
        </motion.div>
      </article>

      {/* Prev/Next navigation */}
      <nav className="max-w-4xl mx-auto px-4 pb-4">
        <div className="flex flex-col sm:flex-row justify-between gap-4">
          {previousPost ? (
            <Link
              to={`/blog/${previousPost.id}`}
              className="group flex-1 inline-flex items-center justify-start rounded-lg border border-gray-200 dark:border-gray-800 p-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
            >
              <FiArrowLeft className="mr-3 w-5 h-5 text-gray-500 group-hover:text-gray-700 dark:group-hover:text-gray-300" />
              <div className="text-left">
                <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Previous</div>
                <div className="text-base font-medium text-gray-900 dark:text-white line-clamp-1">{previousPost.title}</div>
              </div>
            </Link>
          ) : (
            <div className="flex-1" />
          )}

          {nextPost ? (
            <Link
              to={`/blog/${nextPost.id}`}
              className="group flex-1 inline-flex items-center justify-end rounded-lg border border-gray-200 dark:border-gray-800 p-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
            >
              <div className="text-right mr-3">
                <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Next</div>
                <div className="text-base font-medium text-gray-900 dark:text-white line-clamp-1">{nextPost.title}</div>
              </div>
              <FiArrowRight className="w-5 h-5 text-gray-500 group-hover:text-gray-700 dark:group-hover:text-gray-300" />
            </Link>
          ) : (
            <div className="flex-1" />
          )}
        </div>
      </nav>

      {/* Footer */}
      <footer className="max-w-4xl mx-auto px-4 py-16 border-t border-gray-200 dark:border-gray-800 mt-16">
        <div className="text-center text-gray-600 dark:text-gray-400 text-lg">
          <p>Written by {post.author}</p>
          <p className="mt-3">Last updated: {format(new Date(post.date), 'MMMM d, yyyy')}</p>
        </div>
      </footer>
    </motion.div>
  );
}; 